import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, IndianRupee, TrendingUp, AlertTriangle, Wallet, Trash2, Receipt, Download, Share2, MessageCircle, Copy, Mail, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const modes = ["Bank Transfer", "UPI", "Cash", "Cheque", "Other"];

const statusColors = {
  Unpaid: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  Partial: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  Paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  Cancelled: "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-500",
};

function inr(n) {
  return `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Billing() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("outstanding");
  const [detail, setDetail] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", mode: "Bank Transfer", reference: "", notes: "", date: "" });
  const [shareData, setShareData] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ client_id: "", description: "", amount: "", gst_rate: 0, due_date: "" });
  const [editForm, setEditForm] = useState(null); // when set, editing existing invoice

  async function load() {
    let q = "";
    if (tab === "outstanding") q = "?outstanding=true";
    else if (tab === "paid") q = "?status=Paid";
    const { data } = await api.get(`/invoices${q}`);
    setRows(data);
    const { data: s } = await api.get("/billing/summary");
    setSummary(s);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.client_name]));

  async function openDetail(inv) {
    const { data } = await api.get(`/invoices/${inv.id}`);
    setDetail(data);
  }

  async function recordPayment(e) {
    e.preventDefault();
    try {
      const { data } = await api.post(`/invoices/${detail.id}/payments`, {
        ...payForm,
        amount: parseFloat(payForm.amount),
      });
      setDetail(data);
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ amount: "", mode: "Bank Transfer", reference: "", notes: "", date: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  async function removePayment(idx) {
    if (!confirm("Delete this payment?")) return;
    const { data } = await api.delete(`/invoices/${detail.id}/payments/${idx}`);
    setDetail(data);
    load();
  }

  async function openShare() {
    const { data } = await api.get(`/invoices/${detail.id}/share`);
    setShareData(data);
  }
  function copyLink(text, label = "Link") {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  }

  async function delInvoice(id) {
    if (!confirm("Delete this invoice? Task will be marked billable again.")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    setDetail(null);
    load();
  }

  async function createInvoice(e) {
    e.preventDefault();
    try {
      const payload = {
        client_id: newForm.client_id,
        description: newForm.description,
        amount: parseFloat(newForm.amount),
        gst_rate: parseFloat(newForm.gst_rate),
      };
      if (newForm.due_date) payload.due_date = newForm.due_date;
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      setNewOpen(false);
      setNewForm({ client_id: "", description: "", amount: "", gst_rate: 18, due_date: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Wallet className="w-3 h-3" /> Practice revenue
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Billing &amp; Outstanding</h1>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />New Invoice</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Manual invoice</DialogTitle></DialogHeader>
            <form onSubmit={createInvoice} className="space-y-3">
              <div><Label>Client *</Label>
                <Select value={newForm.client_id} onValueChange={v => setNewForm({ ...newForm, client_id: v })}>
                  <SelectTrigger data-testid="inv-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (₹, excl. GST) *</Label><Input data-testid="inv-amount-input" type="number" step="0.01" required value={newForm.amount} onChange={e => setNewForm({ ...newForm, amount: e.target.value })} /></div>
                <div><Label>GST Rate (%)</Label><Input type="number" value={newForm.gst_rate} onChange={e => setNewForm({ ...newForm, gst_rate: e.target.value })} /></div>
                <div className="col-span-2"><Label>Due Date</Label><Input type="date" value={newForm.due_date} onChange={e => setNewForm({ ...newForm, due_date: e.target.value })} /></div>
              </div>
              <DialogFooter><Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create invoice</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-outstanding" className="p-4 border-l-[3px] border-red-500/60">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Outstanding</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(summary?.total_outstanding)}</div>
          <div className="flex items-center gap-1 text-[11px] text-red-600 mt-1"><AlertTriangle className="w-3 h-3" /> {summary?.overdue_count || 0} overdue</div>
        </Card>
        <Card className="p-4 border-l-[3px] border-blue-500/60">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Billed (Month)</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(summary?.billed_month)}</div>
          <div className="flex items-center gap-1 text-[11px] text-blue-600 mt-1"><Receipt className="w-3 h-3" /> Incl. GST</div>
        </Card>
        <Card className="p-4 border-l-[3px] border-emerald-500/60">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Received (Month)</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{inr(summary?.received_month)}</div>
          <div className="flex items-center gap-1 text-[11px] text-emerald-600 mt-1"><TrendingUp className="w-3 h-3" /> Cash in</div>
        </Card>
        <Card className="p-4 border-l-[3px] border-slate-300 dark:border-slate-700">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Invoices</div>
          <div className="font-mono font-semibold text-2xl mt-2 tabular-nums">{summary?.total_invoices || 0}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All Invoices</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">Paid</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card className="p-0">
            <Table className="stripe-hover">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Date</TableHead>
                  <TableHead className="w-24">Due</TableHead>
                  <TableHead className="text-right w-28">Total</TableHead>
                  <TableHead className="text-right w-28">Paid</TableHead>
                  <TableHead className="text-right w-28">Balance</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-sm text-muted-foreground">
                    <Receipt className="w-6 h-6 mx-auto mb-2 opacity-40" />No invoices in this view.
                  </TableCell></TableRow>
                )}
                {rows.map(inv => {
                  const overdue = inv.balance_amount > 0 && inv.due_date < new Date().toISOString().slice(0, 10);
                  return (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="cursor-pointer" onClick={() => openDetail(inv)}>
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell className="text-sm">{clientMap[inv.client_id] || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{inv.description || "—"}</TableCell>
                      <TableCell className="text-xs font-mono">{inv.issue_date ? format(parseISO(inv.issue_date), "dd MMM") : "—"}</TableCell>
                      <TableCell className={`text-xs font-mono ${overdue ? "text-red-600 font-semibold" : ""}`}>{inv.due_date ? format(parseISO(inv.due_date), "dd MMM") : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">{inr(inv.total_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-emerald-600">{inr(inv.paid_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums font-semibold">{inr(inv.balance_amount)}</TableCell>
                      <TableCell><Badge className={`${statusColors[inv.status]} border-0 text-[10px]`}>{inv.status}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Detail */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-mono">{detail.number}</span>
                  <Badge className={`${statusColors[detail.status]} border-0`}>{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground text-xs">Client:</span> <div className="font-medium">{clientMap[detail.client_id]}</div></div>
                  <div><span className="text-muted-foreground text-xs">Issue Date:</span> <div className="font-mono">{detail.issue_date}</div></div>
                  <div className="col-span-2"><span className="text-muted-foreground text-xs">Description:</span> <div>{detail.description || "—"}</div></div>
                </div>

                <Card className="p-4 bg-slate-50 dark:bg-zinc-900">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">Subtotal</div><div className="text-right font-mono tabular-nums">{inr(detail.amount)}</div>
                    <div className="text-muted-foreground">GST @ {detail.gst_rate}%</div><div className="text-right font-mono tabular-nums">{inr(detail.gst_amount)}</div>
                    <div className="font-semibold border-t border-border pt-2">Total</div><div className="text-right font-mono font-semibold tabular-nums border-t border-border pt-2">{inr(detail.total_amount)}</div>
                    <div className="text-emerald-600">Paid</div><div className="text-right font-mono tabular-nums text-emerald-600">{inr(detail.paid_amount)}</div>
                    <div className="font-semibold">Balance Due</div><div className="text-right font-mono font-semibold tabular-nums text-red-600">{inr(detail.balance_amount)}</div>
                  </div>
                </Card>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold">Payment history ({detail.payments?.length || 0})</h4>
                    {detail.balance_amount > 0 && (
                      <Button data-testid="record-payment-btn" size="sm" onClick={() => { setPayForm({ ...payForm, amount: detail.balance_amount }); setPayOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 h-8">
                        <IndianRupee className="w-3 h-3 mr-1" /> Record payment
                      </Button>
                    )}
                  </div>
                  <div className="border border-border rounded-md divide-y divide-border">
                    {(!detail.payments || detail.payments.length === 0) && (
                      <div className="p-3 text-xs text-muted-foreground text-center">No payments recorded yet.</div>
                    )}
                    {detail.payments?.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <div className="font-mono tabular-nums font-semibold w-28">{inr(p.amount)}</div>
                        <div className="text-xs text-muted-foreground">{p.date}</div>
                        <Badge variant="outline" className="text-[10px]">{p.mode}</Badge>
                        {p.reference && <span className="text-[10px] font-mono text-muted-foreground truncate">{p.reference}</span>}
                        <div className="ml-auto text-[10px] text-muted-foreground">by {p.recorded_by_name}</div>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removePayment(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {(me?.role === "Owner" || me?.role === "Partner") && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                    <Button data-testid="edit-invoice-btn" size="sm" variant="outline" onClick={() => setEditForm({
                      number: detail.number,
                      description: detail.description || "",
                      amount: detail.amount,
                      gst_rate: detail.gst_rate,
                      issue_date: detail.issue_date,
                      due_date: detail.due_date,
                    })}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button data-testid="download-pdf-btn" size="sm" variant="outline" asChild>
                      <a href={`${BACKEND_URL}/api/invoices/${detail.id}/pdf`} target="_blank" rel="noreferrer">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
                      </a>
                    </Button>
                    <Button data-testid="share-invoice-btn" size="sm" variant="outline" onClick={openShare} className="bg-blue-50 hover:bg-blue-100 dark:bg-blue-950 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900">
                      <Share2 className="w-3.5 h-3.5 mr-1.5" /> Send / Share
                    </Button>
                    <Button size="sm" variant="ghost" className="ml-auto text-red-600" onClick={() => delInvoice(detail.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment · {detail?.number}</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-3">
            <div><Label>Amount (₹) *</Label><Input data-testid="payment-amount-input" type="number" step="0.01" required value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mode</Label>
                <Select value={payForm.mode} onValueChange={v => setPayForm({ ...payForm, mode: v })}>
                  <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{modes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={e => setPayForm({ ...payForm, date: e.target.value })} /></div>
            </div>
            <div><Label>Reference / Receipt #</Label><Input value={payForm.reference} onChange={e => setPayForm({ ...payForm, reference: e.target.value })} placeholder="UPI ref, cheque no., etc." /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} /></div>
            <DialogFooter><Button data-testid="save-payment-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Save payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!shareData} onOpenChange={o => !o && setShareData(null)}>
        <DialogContent className="max-w-lg">
          {shareData && (
            <>
              <DialogHeader><DialogTitle>Send invoice</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Public payment link</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input data-testid="share-public-url" readOnly value={shareData.public_url} className="font-mono text-xs" />
                    <Button size="icon" variant="outline" onClick={() => copyLink(shareData.public_url, "Link")}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">Client can view invoice + download PDF without login.</div>
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Message preview</Label>
                  <Textarea readOnly rows={5} value={shareData.message} className="mt-1.5 font-mono text-xs" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {shareData.whatsapp ? (
                    <Button data-testid="share-whatsapp-btn" asChild className="bg-emerald-600 hover:bg-emerald-700">
                      <a href={shareData.whatsapp} target="_blank" rel="noreferrer">
                        <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                      </a>
                    </Button>
                  ) : (
                    <Button disabled className="opacity-50">
                      <MessageCircle className="w-4 h-4 mr-1.5" /> No mobile on file
                    </Button>
                  )}
                  <Button variant="outline" asChild>
                    <a href={`mailto:?subject=${encodeURIComponent(shareData.email_subject)}&body=${encodeURIComponent(shareData.email_body)}`}>
                      <Mail className="w-4 h-4 mr-1.5" /> Email
                    </a>
                  </Button>
                  <Button variant="outline" onClick={() => copyLink(shareData.message, "Message")}>
                    <Copy className="w-4 h-4 mr-1.5" /> Copy message
                  </Button>
                  <Button variant="outline" asChild>
                    <a href={shareData.pdf_url} target="_blank" rel="noreferrer">
                      <Download className="w-4 h-4 mr-1.5" /> Open PDF
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Edit Invoice dialog */}
      <Dialog open={!!editForm} onOpenChange={o => !o && setEditForm(null)}>
        <DialogContent className="max-w-lg">
          {editForm && (
            <>
              <DialogHeader><DialogTitle>Edit invoice</DialogTitle></DialogHeader>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const payload = { ...editForm, amount: parseFloat(editForm.amount), gst_rate: parseFloat(editForm.gst_rate) };
                    const { data } = await api.patch(`/invoices/${detail.id}`, payload);
                    setDetail(data);
                    setEditForm(null);
                    toast.success("Invoice updated");
                    load();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                }}
                className="space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Invoice #</Label><Input data-testid="edit-inv-number" required value={editForm.number} onChange={e => setEditForm({ ...editForm, number: e.target.value })} className="font-mono" /></div>
                  <div><Label>Issue Date</Label><Input data-testid="edit-inv-date" type="date" value={editForm.issue_date || ""} onChange={e => setEditForm({ ...editForm, issue_date: e.target.value })} /></div>
                  <div><Label>Due Date</Label><Input type="date" value={editForm.due_date || ""} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} /></div>
                  <div><Label>GST Rate (%)</Label><Input type="number" value={editForm.gst_rate} onChange={e => setEditForm({ ...editForm, gst_rate: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Amount (₹, excl. GST)</Label><Input data-testid="edit-inv-amount" type="number" step="0.01" required value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="save-inv-edit-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">Save changes</Button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
